import { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import { changePassword, getMFAStatus, setupMFA, verifyMFA, disableMFA } from '../services/api';

export default function SettingsPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaSetupData, setMfaSetupData] = useState<{ secret: string; provisioning_uri: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaMessage, setMfaMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    getMFAStatus()
      .then((data) => setMfaEnabled(data.mfa_enabled))
      .catch(() => {})
      .finally(() => setMfaLoading(false));
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setSaving(true);
    try {
      await changePassword(currentPassword, newPassword);
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to change password';
      setMessage({ type: 'error', text: detail });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-enter">
      <Header title="Settings" subtitle="Application configuration" />
      <div className="p-4 md:p-8 space-y-6 max-w-[600px]">

        <Panel title="Change Password">
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full px-3 py-2 rounded text-sm"
                style={{
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 rounded text-sm"
                style={{
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 rounded text-sm"
                style={{
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            {message && (
              <p className="text-sm" style={{ color: message.type === 'success' ? 'var(--netrun-green)' : '#ef4444' }}>
                {message.text}
              </p>
            )}
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded text-sm font-display transition-colors"
              style={{
                background: 'var(--netrun-green)',
                color: 'var(--netrun-black)',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Change Password'}
            </button>
          </form>
        </Panel>

        <Panel title="Two-Factor Authentication">
          {mfaLoading ? (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</p>
          ) : mfaEnabled && !mfaSetupData ? (
            /* MFA is enabled — show disable form */
            <div className="space-y-4">
              <p className="text-sm" style={{ color: 'var(--netrun-green)' }}>
                Two-factor authentication is enabled.
              </p>
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Current Password</label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className="w-full px-3 py-2 rounded text-sm"
                  style={{
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
              {mfaMessage && (
                <p className="text-sm" style={{ color: mfaMessage.type === 'success' ? 'var(--netrun-green)' : '#ef4444' }}>
                  {mfaMessage.text}
                </p>
              )}
              <button
                disabled={mfaBusy || !disablePassword}
                onClick={async () => {
                  setMfaBusy(true);
                  setMfaMessage(null);
                  try {
                    await disableMFA(disablePassword);
                    setMfaEnabled(false);
                    setDisablePassword('');
                    setMfaMessage({ type: 'success', text: 'Two-factor authentication disabled.' });
                  } catch (err: any) {
                    setMfaMessage({ type: 'error', text: err?.response?.data?.detail || 'Failed to disable MFA' });
                  } finally {
                    setMfaBusy(false);
                  }
                }}
                className="px-4 py-2 rounded text-sm font-display transition-colors"
                style={{ background: '#ef4444', color: '#fff', opacity: mfaBusy ? 0.6 : 1 }}
              >
                {mfaBusy ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          ) : mfaSetupData ? (
            /* Setup in progress — show secret + verify form */
            <div className="space-y-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Scan the URI below with your authenticator app, then enter the 6-digit code to activate.
              </p>
              <div
                className="p-3 rounded text-xs break-all select-all"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
              >
                {mfaSetupData.provisioning_uri}
              </div>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Secret key: <span className="font-mono select-all">{mfaSetupData.secret}</span>
              </p>
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full px-3 py-2 rounded text-sm font-mono tracking-widest"
                  style={{
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
              {mfaMessage && (
                <p className="text-sm" style={{ color: mfaMessage.type === 'success' ? 'var(--netrun-green)' : '#ef4444' }}>
                  {mfaMessage.text}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  disabled={mfaBusy || mfaCode.length !== 6}
                  onClick={async () => {
                    setMfaBusy(true);
                    setMfaMessage(null);
                    try {
                      await verifyMFA(mfaCode);
                      setMfaEnabled(true);
                      setMfaSetupData(null);
                      setMfaCode('');
                      setMfaMessage({ type: 'success', text: 'Two-factor authentication enabled.' });
                    } catch (err: any) {
                      setMfaMessage({ type: 'error', text: err?.response?.data?.detail || 'Invalid code' });
                    } finally {
                      setMfaBusy(false);
                    }
                  }}
                  className="px-4 py-2 rounded text-sm font-display transition-colors"
                  style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', opacity: mfaBusy ? 0.6 : 1 }}
                >
                  {mfaBusy ? 'Verifying...' : 'Verify & Activate'}
                </button>
                <button
                  onClick={() => { setMfaSetupData(null); setMfaCode(''); setMfaMessage(null); }}
                  className="px-4 py-2 rounded text-sm font-display transition-colors"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* MFA not enabled — show enable button */
            <div className="space-y-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Add an extra layer of security to your account with a time-based one-time password (TOTP).
              </p>
              {mfaMessage && (
                <p className="text-sm" style={{ color: mfaMessage.type === 'success' ? 'var(--netrun-green)' : '#ef4444' }}>
                  {mfaMessage.text}
                </p>
              )}
              <button
                disabled={mfaBusy}
                onClick={async () => {
                  setMfaBusy(true);
                  setMfaMessage(null);
                  try {
                    const data = await setupMFA();
                    setMfaSetupData({ secret: data.secret, provisioning_uri: data.provisioning_uri });
                  } catch (err: any) {
                    setMfaMessage({ type: 'error', text: err?.response?.data?.detail || 'Failed to start MFA setup' });
                  } finally {
                    setMfaBusy(false);
                  }
                }}
                className="px-4 py-2 rounded text-sm font-display transition-colors"
                style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', opacity: mfaBusy ? 0.6 : 1 }}
              >
                {mfaBusy ? 'Setting up...' : 'Enable 2FA'}
              </button>
            </div>
          )}
        </Panel>

        <Panel title="AI Provider">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Provider</span>
              <span className="text-sm font-display" style={{ color: 'var(--netrun-green)' }}>Gemini 2.5 Flash</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Voice</span>
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Kore (Gemini Live)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>RAG Collection</span>
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>kog_sales</span>
            </div>
          </div>
        </Panel>

        <Panel title="Infrastructure">
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>Backend</span>
              <span style={{ color: 'var(--text-primary)' }}>Cloud Run (us-central1)</span>
            </div>
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>Database</span>
              <span style={{ color: 'var(--text-primary)' }}>Cloud SQL PostgreSQL 15</span>
            </div>
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>Documents</span>
              <span style={{ color: 'var(--text-primary)' }}>GCS + pgvector</span>
            </div>
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>Voice</span>
              <span style={{ color: 'var(--text-primary)' }}>Charlotte Gemini Live</span>
            </div>
          </div>
        </Panel>

        <Panel title="About">
          <div className="space-y-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            <p>KOG v2.0 — Headless Google Cloud Native</p>
            <p>Zero Azure dependencies. Single AI provider (Gemini).</p>
            <p style={{ color: 'var(--netrun-green)' }}>Netrun Systems</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
